import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { PRIMARY_ROLES } from '@/data/audiences';

interface Props {
  /** `hero` = glass cards on the dark hero. `light` = compact strip at the top of landing pages. */
  variant?: 'hero' | 'light';
  className?: string;
}

/**
 * "WHERE DO I BELONG?" — answered in the first screen.
 *
 * Three large, labelled doors (Homeowner / Contractor / Subcontractor). Each is
 * ONE click into the real workflow; signed-in visitors skip signup and land on
 * the action itself. On phones they render as full-width rows (~64px each) so
 * all three sit above the fold under the headline.
 */
const RoleQuickStart: React.FC<Props> = ({ variant = 'hero', className = '' }) => {
  const { path, lang } = useI18n();
  const { profile } = useAuth();
  const L = (v: { en: string; es: string }) => (lang === 'es' ? v.es : v.en);
  const hero = variant === 'hero';

  return (
    <nav aria-label={lang === 'es' ? 'Elija su rol' : 'Choose your role'} className={className}>
      <p
        className={`mb-2.5 font-extrabold uppercase tracking-wider ${
          hero ? 'text-sm text-orange-300 sm:text-base' : 'text-xs text-slate-500'
        }`}
      >
        {lang === 'es' ? '¿Quién es usted? Empiece en un clic' : 'Who are you? Start in one click'}
      </p>
      {/*
        PRIMARY ACTION OF THE PAGE. On the hero these are the largest, highest-
        contrast elements above the fold: solid white cards with an orange
        ring, a 20px action line and a full-width coloured button. Everything
        else in the hero (browse link, stats) is deliberately quieter.
      */}
      <ul className="grid gap-2.5 sm:grid-cols-3 sm:gap-4">
        {PRIMARY_ROLES.map((r) => {
          const to = path(profile ? r.memberTo : r.guestTo);
          return (
            <li key={r.key}>
              <Link
                to={to}
                data-role-entry={r.key}
                style={{ touchAction: 'manipulation' }}
                className={`group flex h-full min-h-[64px] items-center gap-3 rounded-2xl transition-all duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 sm:flex-col sm:items-start sm:gap-2.5 ${
                  hero
                    ? 'bg-white p-3.5 shadow-2xl shadow-black/30 ring-2 ring-orange-400/70 hover:ring-4 hover:ring-orange-400 sm:p-6'
                    : 'border border-slate-200 bg-white p-3 shadow-sm hover:border-slate-300 hover:shadow-card sm:p-5'
                }`}
              >
                <span
                  className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm ${hero ? 'sm:h-14 sm:w-14' : 'sm:h-12 sm:w-12'} ${r.tone}`}
                >
                  <r.icon className={`h-5 w-5 ${hero ? 'sm:h-7 sm:w-7' : 'sm:h-6 sm:w-6'}`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-bold uppercase tracking-wide text-slate-500 sm:text-[13px]">
                    {L(r.title)}
                  </span>
                  <span className={`mt-0.5 block font-extrabold leading-snug text-slate-900 ${hero ? 'text-lg sm:text-2xl' : 'text-base sm:text-lg'}`}>
                    {L(r.action)}
                  </span>
                  <span className="mt-1 hidden text-xs leading-relaxed text-slate-500 sm:block">{L(r.hint)}</span>
                </span>
                <span
                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-white shadow-md transition-transform group-hover:translate-x-1 sm:mt-1 sm:h-auto sm:w-full sm:gap-1.5 sm:rounded-xl sm:px-4 sm:py-3 sm:text-base sm:font-extrabold ${r.tone}`}
                >
                  <span className="hidden sm:inline">{lang === 'es' ? 'Empezar' : 'Start'}</span>
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      <p className={`mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold ${hero ? 'text-slate-300' : 'text-slate-500'}`}>
        <Link
          to={path(profile ? '/dashboard' : '/register?role=homeowner&type=commercial')}
          className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
        >
          <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
          {lang === 'es' ? 'Propiedad comercial: publicar proyecto' : 'Commercial property? Post a project'}
        </Link>
        {!profile && (
          <Link to={path('/login')} className="underline-offset-2 hover:underline">
            {lang === 'es' ? '¿Ya tiene cuenta? Inicie sesión' : 'Already a member? Sign in'}
          </Link>
        )}
      </p>
    </nav>
  );
};

export default RoleQuickStart;
